var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import StyledButton from './StyledButton';
import LoadingDots from '../LoadingDots';
function usePrevious(value) {
    var ref = React.useRef();
    React.useEffect(function () {
        ref.current = value;
    });
    return ref.current;
}
var validSizes = new Set(['small', 'medium', 'large']);
var validStyles = new Set(['primary', 'secondary', 'link', 'link-secondary']);
var defaultBtnSize = 'medium';
var defaultBtnStyle = 'primary';
var ClydeButton = function (props) {
    var _a;
    var children = props.children, _b = props.btnStyle, btnStyle = _b === void 0 ? defaultBtnStyle : _b, _c = props.btnSize, btnSize = _c === void 0 ? defaultBtnSize : _c, _d = props.className, className = _d === void 0 ? '' : _d, _e = props.type, type = _e === void 0 ? (props.onClick ? 'button' : 'submit') : _e, _f = props.processing, processing = _f === void 0 ? false : _f, successText = props.successText, rest = __rest(props, ["children", "btnStyle", "btnSize", "className", "type", "processing", "successText"]);
    var prevProcessing = usePrevious(processing);
    var _g = React.useState(false), successTextVisible = _g[0], setSuccessTextVisible = _g[1];
    // Hide success text after showing for 600ms
    React.useEffect(function () {
        if (successTextVisible) {
            setTimeout(function () {
                setSuccessTextVisible(false);
            }, 600);
        }
    }, [successTextVisible]);
    // If visible, automatically hide success text when processing
    React.useEffect(function () {
        if (processing && successTextVisible) {
            setSuccessTextVisible(false);
        }
    }, [processing, successTextVisible]);
    // Show success text after processing changes from TRUE back to FALSE
    React.useEffect(function () {
        if (prevProcessing && !processing && successText) {
            setSuccessTextVisible(true);
        }
    }, [prevProcessing, processing, setSuccessTextVisible, successText]);
    // Make sure btnStyle is valid
    // NOTE: "link" style doesn't have enough a11y contrast at small/medium sizes
    var sanitizedBtnStyle = React.useMemo(function () {
        if (!validStyles.has(btnStyle)) {
            return defaultBtnStyle;
        }
        if (btnStyle === 'link' && btnSize !== 'large') {
            // eslint-disable-next-line no-console
            console.warn('"link" style not allowed for medium or small buttons for accessibility reasons');
            return 'link-secondary';
        }
        return btnStyle;
    }, [btnStyle, btnSize]);
    // Make sure btnSize is valid
    var sanitizedBtnSize = React.useMemo(function () {
        if (!validSizes.has(btnSize)) {
            return defaultBtnSize;
        }
        return btnSize;
    }, [btnSize]);
    // Conditionally render loading indicator if processing
    var maybeRenderLoadingIndicator = function () {
        if (!processing) {
            return null;
        }
        if (btnStyle === 'primary') {
            return React.createElement(LoadingDots, { dotColor: "clydeWhite" });
        }
        return React.createElement(LoadingDots, { dotColor: "clydeBlack" });
    };
    // Conditionally render success text
    var maybeRenderSuccessText = function () {
        if (!successTextVisible) {
            return null;
        }
        return React.createElement("span", { className: "success" }, successText);
    };
    var _className = classNames(className, (_a = {},
        _a["size-".concat(sanitizedBtnSize)] = true,
        _a["style-".concat(sanitizedBtnStyle)] = true,
        _a['label-hidden'] = processing || successTextVisible,
        _a));
    return (React.createElement(StyledButton, __assign({ type: type, className: _className, onMouseUp: function (e) { e.currentTarget.blur(); } }, rest),
        React.createElement(React.Fragment, null,
            maybeRenderLoadingIndicator(),
            maybeRenderSuccessText(),
            React.createElement("span", { className: "label" }, children))));
};
export default ClydeButton;
